import React, { useState } from "react";
import {
  Plus,
  Loader2,
  Trash2,
  ArrowLeft,
  FileText,
  Upload,
  Send,
  ShieldCheck,
  XCircle,
  Ban,
  AlertTriangle,
  Calendar,
  Clock,
  ChevronDown,
  ChevronRight,
  Pencil,
  Save,
  X,
  File,
  CheckCircle,
  Eye,
  FilePlus,
  History,
  User,
  Building2,
} from "lucide-react";
import { StatusBadge } from "../../components/StatusBadge";
import { SkeletonList } from "../../components/Skeleton";
import {
  FormField,
  FormInput,
  FormTextarea,
  FormSelect,
} from "../../components/Modal";
import { RBACAction } from "../../components/RBACButton";
import { API_URL } from "../../../constants";
import { useAuthStore } from "../../../stores/authStore";
import {
  downloadGovernanceDocumentFile,
  previewGovernanceDocumentFile,
  type GovernanceDocumentResponse,
  type HistoryEvent,
} from "../../../api/frameworksApi";
import { DOC_TYPE_COLORS } from "./constants";
import {
  formatDate,
  formatDateTime,
  formatDocType,
  formatFileSize,
  getHistoryActorName,
  lifecycleActionLabel,
  mapLifecycleToVariant,
} from "./utils";
import { InlineLifecycle } from "./InlineLifecycle";
import { PreviewModal } from "./PreviewModal";
import { StatCard } from "./StatCard";
import { VersionRow } from "./VersionRow";

/* ── Governance Document Detail Component ── */
export function GovernanceDocumentDetail({
  doc,
  loading,
  editMode,
  editTitle,
  editDescription,
  editReviewCycle,
  editCustomDays,
  savingEdit,
  onBack,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onEditTitleChange,
  onEditDescriptionChange,
  onEditReviewCycleChange,
  onEditCustomDaysChange,
  onLifecycleAction,
  onUpload,
  onRemoveFile,
  onDelete,
  isEditable,
  history,
  historyLoading,
}: {
  doc: GovernanceDocumentResponse;
  loading: boolean;
  editMode: boolean;
  editTitle: string;
  editDescription: string;
  editReviewCycle: "none" | "90" | "180" | "365" | "custom";
  editCustomDays: string;
  savingEdit: boolean;
  onBack: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditTitleChange: (v: string) => void;
  onEditDescriptionChange: (v: string) => void;
  onEditReviewCycleChange: (
    v: "none" | "90" | "180" | "365" | "custom",
  ) => void;
  onEditCustomDaysChange: (v: string) => void;
  onLifecycleAction: (action: string) => void;
  onUpload: () => void;
  onRemoveFile: () => void;
  onDelete: () => void;
  isEditable: boolean;
  history: HistoryEvent[];
  historyLoading: boolean;
}) {
  const [showPreview, setShowPreview] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  return (
    <div className="space-y-6">
      {/* Breadcrumb + inline lifecycle */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Content Registry
        </button>
        <ChevronRight className="size-3.5 text-muted-foreground" />
        <span className="text-sm font-medium">{doc.title}</span>
        <InlineLifecycle currentStatus={doc.status} />
      </div>

      {/* Title + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1>{doc.title}</h1>
            {doc.reviewDueSoon && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-xs font-bold text-amber-600 dark:text-amber-400 ring-1 ring-amber-200/60 dark:ring-amber-500/20">
                <AlertTriangle className="size-3" />
                Review Due Soon
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {formatDocType(doc.documentType)}
            {` · v${doc.currentVersion}`}
            {doc.status === "RETIRED" && " · Withdrawn"}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0 flex-wrap">
          {/* Edit button for DRAFT */}
          {isEditable && !editMode && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              <Pencil className="size-4" /> Edit
            </button>
          )}
          {editMode && (
            <>
              <button
                onClick={onCancelEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                <X className="size-4" /> Cancel
              </button>
              <button
                onClick={onSaveEdit}
                disabled={savingEdit}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
              >
                {savingEdit ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}{" "}
                Save
              </button>
            </>
          )}

          {/* Lifecycle action buttons */}
          {doc.status === "DRAFT" && !editMode && (
            <>
              {!doc.currentVersionInfo?.hasFile && (
                <button
                  onClick={onUpload}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  <Upload className="size-4" /> Upload File
                </button>
              )}
              {doc.currentVersionInfo?.hasFile && (
                <button
                  onClick={() => onLifecycleAction("submit")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-md shadow-blue-500/20"
                >
                  <Send className="size-4" /> Submit for Review
                </button>
              )}
            </>
          )}
          {doc.status === "IN_REVIEW" && (
            <RBACAction module="document" action="approve">
              <>
                <button
                  onClick={() => onLifecycleAction("approve")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors shadow-md shadow-emerald-500/20"
                >
                  <ShieldCheck className="size-4" /> Approve
                </button>
                <button
                  onClick={() => onLifecycleAction("reject")}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors shadow-md shadow-red-500/20"
                >
                  <XCircle className="size-4" /> Reject
                </button>
              </>
            </RBACAction>
          )}
          {doc.status === "APPROVED" && (
            <RBACAction module="document" action="approve">
              <button
                onClick={() => onLifecycleAction("publish")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-md shadow-blue-500/20"
              >
                <Send className="size-4" /> Publish
              </button>
            </RBACAction>
          )}
          {(doc.status === "PUBLISHED" || doc.status === "REVIEW_OVERDUE") && (
            <>
              <button
                onClick={() => onLifecycleAction("new-version")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                <FilePlus className="size-4" /> New Version
              </button>
              <button
                onClick={() => onLifecycleAction("retire")}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-rose-700 hover:bg-rose-800 text-white text-sm font-bold transition-colors shadow-md shadow-rose-500/20"
              >
                <Ban className="size-4" /> Withdraw
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <SkeletonList rows={3} />
      ) : (
        <>
          {/* Rejection banner */}
          {doc.rejectionReason && doc.status === "DRAFT" && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
              <AlertTriangle className="size-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Document Rejected
                </p>
                <p className="text-sm text-red-600 dark:text-red-400/80 mt-0.5">
                  {doc.rejectionReason}
                </p>
                {doc.rejectedAt && (
                  <p className="text-xs text-red-500/70 mt-1">
                    Rejected on {formatDate(doc.rejectedAt)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Overview banner */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-indigo-50/30 dark:to-indigo-500/[0.03] overflow-hidden shadow-sm">
            <div className="p-6">
              {editMode ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField label="Title" required>
                    <FormInput
                      value={editTitle}
                      onChange={onEditTitleChange}
                      placeholder="Document title"
                    />
                  </FormField>
                  <div className="flex gap-3">
                    <div
                      className={
                        editReviewCycle === "custom" ? "flex-1" : "w-full"
                      }
                    >
                      <FormField label="Review Cycle" required>
                        <FormSelect
                          options={[
                            { value: "none", label: "No review required" },
                            { value: "90", label: "Quarterly" },
                            { value: "180", label: "Half-Yearly" },
                            { value: "365", label: "Annually" },
                            { value: "custom", label: "Custom" },
                          ]}
                          value={editReviewCycle}
                          onChange={(v) =>
                            onEditReviewCycleChange(
                              v as "none" | "90" | "180" | "365" | "custom",
                            )
                          }
                        />
                      </FormField>
                    </div>
                    {editReviewCycle === "custom" && (
                      <div className="w-28">
                        <FormField label="Days" required>
                          <FormInput
                            type="number"
                            value={editCustomDays}
                            onChange={onEditCustomDaysChange}
                            placeholder="Days"
                          />
                        </FormField>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <FormField label="Description">
                      <FormTextarea
                        value={editDescription}
                        onChange={onEditDescriptionChange}
                        placeholder="Document description"
                        rows={3}
                      />
                    </FormField>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Status chips row */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${DOC_TYPE_COLORS[doc.documentType] ?? "bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-white/50"}`}
                    >
                      <FileText className="size-3" />
                      {formatDocType(doc.documentType)}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-white/[0.06] text-xs font-bold text-slate-600 dark:text-white/50 ring-1 ring-slate-200/60 dark:ring-white/[0.08]">
                      v{doc.currentVersion}
                    </span>
                    <StatusBadge
                      status={
                        mapLifecycleToVariant(doc.status) as Parameters<
                          typeof StatusBadge
                        >[0]["status"]
                      }
                      dot={false}
                    />
                  </div>

                  {/* Stat cards row — shared detail-page order (see the Catalog
                      and Profile detail headers): created, organization,
                      published, last updated, status, then module-specific. */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Created Date"
                      value={formatDate(doc.createdAt)}
                      color="slate"
                    />
                    {doc.owningOrgUnitName && (
                      <StatCard
                        icon={<Building2 className="size-4" />}
                        label="Organization"
                        value={doc.owningOrgUnitName}
                        color="slate"
                        tooltip={doc.owningOrgUnitName}
                      />
                    )}
                    <StatCard
                      icon={<Calendar className="size-4" />}
                      label="Published Date"
                      value={
                        doc.publishedAt
                          ? formatDate(doc.publishedAt)
                          : "Not yet published"
                      }
                      dim={!doc.publishedAt}
                      color="emerald"
                    />
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Last Updated Date"
                      value={formatDate(doc.updatedAt)}
                      color="slate"
                    />
                    <StatCard
                      icon={<Eye className="size-4" />}
                      label="Status"
                      value={(doc.status ?? "").replace(/_/g, " ").toLowerCase()}
                      color="indigo"
                      valueClassName="capitalize"
                    />
                    <StatCard
                      icon={<Clock className="size-4" />}
                      label="Next Review Date"
                      value={
                        doc.reviewDate
                          ? formatDate(doc.reviewDate)
                          : doc.requiresReview
                            ? "Not scheduled"
                            : "No review required"
                      }
                      dim={!doc.reviewDate}
                      color={doc.status === "REVIEW_OVERDUE" ? "amber" : "sky"}
                    />
                  </div>

                  {/* Retirement reason */}
                  {doc.retirementReason && (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Ban className="size-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Retirement reason:</strong>{" "}
                        {doc.retirementReason}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Current Version File */}
          {doc.currentVersionInfo && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02]">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <File className="size-4 text-indigo-500 dark:text-indigo-400" />
                  Current Version
                </h3>
                <div className="flex items-center gap-2">
                  {doc.currentVersionInfo.hasFile ? (
                    <>
                      <button
                        onClick={() => setShowPreview(true)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                        title="View file"
                      >
                        <Eye className="size-3" /> View
                      </button>
                      {isEditable && (
                        <>
                          <button
                            onClick={onUpload}
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-border hover:bg-accent transition-colors text-foreground"
                            title="Replace file"
                          >
                            <Upload className="size-3" /> Replace
                          </button>
                          <button
                            onClick={onRemoveFile}
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                            title="Delete file"
                          >
                            <Trash2 className="size-3" /> Delete
                          </button>
                        </>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
              <div className="p-4">
                {doc.currentVersionInfo.hasFile ? (
                  <div className="flex items-center gap-4">
                    <File className="size-10 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {doc.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {doc.currentVersionInfo.fileName} · {formatFileSize(doc.currentVersionInfo.fileSize)}
                      </p>
                      {doc.description && (
                        <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No file uploaded yet</p>
                )}
              </div>
            </div>
          )}

          {/* Version History */}
          {doc.versions && doc.versions.length > 1 && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                onClick={() => setExpandedVersions(!expandedVersions)}
                className="w-full flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02] hover:bg-[#eef2f7] dark:hover:bg-white/[0.04] transition-colors"
              >
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <File className="size-4 text-indigo-500 dark:text-indigo-400" />
                  Version History
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    {doc.versions.length}
                  </span>
                </h3>
                <ChevronDown
                  className={`size-4 text-muted-foreground transition-transform ${expandedVersions ? "rotate-180" : ""}`}
                />
              </button>
              {expandedVersions && (
                <div className="divide-y divide-border">
                  {doc.versions.map((ver) => (
                    <VersionRow
                      key={ver.versionNumber}
                      version={ver}
                      docId={doc.id}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activity History */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              onClick={() => setExpandedHistory(!expandedHistory)}
              className="w-full flex items-center justify-between p-4 border-b border-border bg-[#f5f7fb] dark:bg-white/[0.02] hover:bg-[#eef2f7] dark:hover:bg-white/[0.04] transition-colors"
            >
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <History className="size-4 text-indigo-500 dark:text-indigo-400" />
                Activity History
                {history.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    {history.length}
                  </span>
                )}
              </h3>
              <ChevronDown
                className={`size-4 text-muted-foreground transition-transform ${expandedHistory ? "rotate-180" : ""}`}
              />
            </button>
            {expandedHistory && (
              <div className="p-4">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="size-5 animate-spin text-muted-foreground" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="size-10 rounded-full bg-muted/50 flex items-center justify-center mb-2">
                      <History className="size-4 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">No activity recorded yet</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

                    <div className="space-y-4">
                      {history.map((event) => (
                        <div key={event.id} className="relative flex gap-3">
                          {/* Timeline dot */}
                          <div
                            className={`relative z-10 flex-shrink-0 size-6 rounded-full flex items-center justify-center ${event.eventType === "CREATED"
                                ? "bg-emerald-100 dark:bg-emerald-500/20"
                                : event.eventType === "SUBMITTED"
                                  ? "bg-blue-100 dark:bg-blue-500/20"
                                  : event.eventType === "APPROVED"
                                    ? "bg-emerald-100 dark:bg-emerald-500/20"
                                    : event.eventType === "REJECTED"
                                      ? "bg-red-100 dark:bg-red-500/20"
                                      : event.eventType === "PUBLISHED"
                                        ? "bg-emerald-100 dark:bg-emerald-500/20"
                                        : event.eventType === "RETIRED"
                                          ? "bg-slate-100 dark:bg-slate-500/20"
                                          : event.eventType ===
                                            "VERSION_CREATED"
                                            ? "bg-indigo-100 dark:bg-indigo-500/20"
                                            : "bg-slate-100 dark:bg-slate-500/20"
                              }`}
                          >
                            {event.eventType === "CREATED" && (
                              <Plus className="size-3 text-emerald-600 dark:text-emerald-400" />
                            )}
                            {event.eventType === "SUBMITTED" && (
                              <Send className="size-3 text-blue-600 dark:text-blue-400" />
                            )}
                            {event.eventType === "APPROVED" && (
                              <CheckCircle className="size-3 text-emerald-600 dark:text-emerald-400" />
                            )}
                            {event.eventType === "REJECTED" && (
                              <XCircle className="size-3 text-red-600 dark:text-red-400" />
                            )}
                            {event.eventType === "PUBLISHED" && (
                              <ShieldCheck className="size-3 text-emerald-600 dark:text-emerald-400" />
                            )}
                            {event.eventType === "RETIRED" && (
                              <Ban className="size-3 text-slate-600 dark:text-slate-400" />
                            )}
                            {event.eventType === "VERSION_CREATED" && (
                              <FilePlus className="size-3 text-indigo-600 dark:text-indigo-400" />
                            )}
                            {![
                              "CREATED",
                              "SUBMITTED",
                              "APPROVED",
                              "REJECTED",
                              "PUBLISHED",
                              "RETIRED",
                              "VERSION_CREATED",
                            ].includes(event.eventType) && (
                                <History className="size-3 text-slate-600 dark:text-slate-400" />
                              )}
                          </div>

                          {/* Event content */}
                          <div className="flex-1 min-w-0 pb-4">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {event.eventDescription ||
                                    event.eventType.replace(/_/g, " ")}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <User className="size-3" />
                                    <span>
                                      {getHistoryActorName(event, null)}
                                    </span>
                                  </div>
                                  {event.fromStatus && event.toStatus && event.fromStatus !== event.toStatus && (
                                    <div className="flex items-center gap-1 text-xs">
                                      <span className="px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground font-medium">
                                        {event.fromStatus.replace(/_/g, " ")}
                                      </span>
                                      <ChevronRight className="size-3 text-muted-foreground" />
                                      <span className="px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground font-medium">
                                        {event.toStatus.replace(/_/g, " ")}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <span className="flex-shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                                {formatDateTime(event.performedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Preview modal */}
      {showPreview && doc.currentVersionInfo?.hasFile && (
        <PreviewModal
          url={`${previewGovernanceDocumentFile(doc.id)}?v=${encodeURIComponent(
            [
              doc.currentVersionInfo.versionNumber,
              doc.currentVersionInfo.fileSize ?? "",
              doc.currentVersionInfo.fileName ?? "",
              doc.currentVersionInfo.createdAt ?? "",
            ].join("-"),
          )}`}
          fileName={doc.currentVersionInfo.fileName}
          downloadUrl={downloadGovernanceDocumentFile(doc.id)}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
